import { PropsWithChildren } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { useToast } from '@givebutter/primitives/toast';
import { pluralize } from '@givebutter/util';
import { faCircleCheck, faCircleExclamation } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { deleteImport } from 'api/imports';
import { DeleteContactStats } from 'assets/js/pages/account-settings/imports/DeleteContactStats';
import { DeleteImportFormValues } from 'assets/js/pages/account-settings/imports/types';
import * as Form from 'components/form';
import { importFormatted } from 'components/imports/importStrategy';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useDeletionStats } from 'customHooks/useDeletionStats';
import { useForceRender } from 'customHooks/useForceRender';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { Import } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface DeleteImportAlertDialogProps extends PropsWithChildren {
    importRecord: Import;
}

const DeleteImportAlertDialog = (props: DeleteImportAlertDialogProps) => {
    const toast = useToast();
    const { importRecord, children } = props;
    const { accountId } = useCurrentAccount();
    const { key, forceRender } = useForceRender();
    const methods = Form.useGivebutterForm<DeleteImportFormValues>({
        defaultValues: {
            includeContacts: false,
            includeTransactions: false,
        },
    });
    const { path, control, handleSubmit } = methods;
    const {
        deletionStats,
        hasContacts,
        hasTransactions,
        hasIgnoredContacts,
        hasActivities,
        hasSoftCredits,
        hasNone,
        hasAny,
    } = useDeletionStats(importRecord);

    const { mutate: submit, isPending: isDeleting } = useMutation({
        mutationFn: (values: DeleteImportFormValues) =>
            deleteImport(accountId, importRecord.id, {
                type: importRecord.resource,
                includeContacts: values.includeContacts,
                includeTransactions: values.includeTransactions,
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.imports(accountId) });
            toast.success('Data deletion is in progress.');
            forceRender();
        },
        onError: (error: GivebutterAxiosError) =>
            toast.error(error.response?.data.message ?? 'Unknown error occurred'),
    });

    return (
        <AlertDialog.Root type={'error'} key={key}>
            {children}
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <Form.GivebutterFormProvider {...methods}>
                    <form onSubmit={handleSubmit(submit as SubmitHandler<DeleteImportFormValues>)}>
                        <AlertDialog.Content>
                            <AlertDialog.Header>
                                <AlertDialog.Icon />
                                <AlertDialog.Close />
                            </AlertDialog.Header>
                            <AlertDialog.Body>
                                <AlertDialog.Title>
                                    Delete imported{' '}
                                    {importFormatted(importRecord.resource).toLowerCase()}?
                                </AlertDialog.Title>
                                <AlertDialog.Description>
                                    {hasNone
                                        ? 'No data is eligible to be deleted. Either the data in this import has already been deleted or the records contain transactions or other related data that prevent them from being deleted.'
                                        : 'The following data will be deleted from your account:'}
                                </AlertDialog.Description>
                                <div className="flex flex-col gap-2">
                                    {(importRecord.resource === 'contacts' ||
                                        importRecord.resource === 'companies') && (
                                        <DeleteContactStats importRecord={importRecord} />
                                    )}

                                    {importRecord.resource === 'contact_activities' && (
                                        <>
                                            {hasActivities && (
                                                <div className="flex items-center gap-2">
                                                    <FontAwesomeIcon
                                                        icon={faCircleCheck}
                                                        className="text-red-500"
                                                    />
                                                    <div className="font-medium text-gray-700">
                                                        {deletionStats.activities} {'contact'}{' '}
                                                        {pluralize(
                                                            deletionStats.activities,
                                                            'activity',
                                                            'ies',
                                                        )}{' '}
                                                        will be deleted
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}

                                    {importRecord.resource === 'soft_credits' && (
                                        <>
                                            {hasSoftCredits && (
                                                <div className="flex items-center gap-2">
                                                    <FontAwesomeIcon
                                                        icon={faCircleCheck}
                                                        className="text-red-500"
                                                    />
                                                    <div className="font-medium text-gray-700">
                                                        {deletionStats.soft_credits} {'soft credit'}{' '}
                                                        {pluralize(
                                                            deletionStats.soft_credits,
                                                            'entry',
                                                            'ies',
                                                        )}{' '}
                                                        will be deleted
                                                    </div>
                                                </div>
                                            )}
                                            {hasContacts && (
                                                <div className="flex flex-col">
                                                    <div className="flex gap-2">
                                                        <Form.UseFormCheckboxInput
                                                            control={control}
                                                            name={path('includeContacts')}
                                                            className="mt-1"
                                                        />
                                                        <div>
                                                            <Form.Label
                                                                name={path('includeContacts')}
                                                            >
                                                                Also delete associated contacts
                                                            </Form.Label>
                                                            <div className="text-gray-600">
                                                                {deletionStats.contacts}{' '}
                                                                {'associated contact'}{' '}
                                                                {pluralize(
                                                                    deletionStats.contacts,
                                                                    'entry',
                                                                )}{' '}
                                                                {deletionStats.contacts === 1
                                                                    ? 'is'
                                                                    : 'are'}{' '}
                                                                associated with these soft credits
                                                                and can be deleted. Leave this box
                                                                unchecked to keep the contacts.
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {hasIgnoredContacts && (
                                                <div className="flex flex-col">
                                                    <div className="flex items-center gap-2">
                                                        <FontAwesomeIcon
                                                            icon={faCircleExclamation}
                                                            className="text-gray-500"
                                                        />
                                                        <div className="font-medium text-gray-700">
                                                            {deletionStats.ignored_contacts}{' '}
                                                            {pluralize(
                                                                deletionStats.ignored_contacts,
                                                                'contact',
                                                            )}{' '}
                                                            cannot be deleted
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="w-4.5">&nbsp;</span>
                                                        <div className="text-gray-600">
                                                            Contacts with transactions processed
                                                            through Givebutter cannot be deleted.
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}

                                    {importRecord.resource === 'transactions' && (
                                        <>
                                            {hasTransactions && (
                                                <div className="flex items-center gap-2">
                                                    <FontAwesomeIcon
                                                        icon={faCircleCheck}
                                                        className="text-red-500"
                                                    />
                                                    <div className="font-medium text-gray-700">
                                                        {deletionStats.transactions}{' '}
                                                        {pluralize(
                                                            deletionStats.transactions,
                                                            'transaction',
                                                        )}{' '}
                                                        will be deleted
                                                    </div>
                                                </div>
                                            )}

                                            {hasContacts && (
                                                <div className="flex flex-col">
                                                    <div className="flex gap-2">
                                                        <Form.UseFormCheckboxInput
                                                            control={control}
                                                            name={path('includeContacts')}
                                                            className="mt-1"
                                                        />
                                                        <div>
                                                            <Form.Label
                                                                name={path('includeContacts')}
                                                            >
                                                                Also delete associated contacts
                                                            </Form.Label>

                                                            <div className="text-gray-600">
                                                                {deletionStats.contacts}{' '}
                                                                {pluralize(
                                                                    deletionStats.contacts,
                                                                    'contact',
                                                                )}{' '}
                                                                {deletionStats.contacts === 1
                                                                    ? 'is'
                                                                    : 'are'}{' '}
                                                                associated with these imported
                                                                transactions and can be deleted.
                                                                Leave this box unchecked to keep the
                                                                contacts.{' '}
                                                                {hasIgnoredContacts && (
                                                                    <>
                                                                        {
                                                                            deletionStats.ignored_contacts
                                                                        }{' '}
                                                                        {pluralize(
                                                                            deletionStats.ignored_contacts,
                                                                            'contact',
                                                                        )}{' '}
                                                                        cannot be deleted because
                                                                        they have processed
                                                                        transactions on Givebutter
                                                                        or have imported recurring
                                                                        donations.
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {!hasContacts && hasIgnoredContacts && (
                                                <div className="flex flex-col">
                                                    <div className="flex items-center gap-2">
                                                        <FontAwesomeIcon
                                                            icon={faCircleExclamation}
                                                            className="text-gray-500"
                                                        />
                                                        <div className="font-medium text-gray-700">
                                                            {deletionStats.ignored_contacts}{' '}
                                                            {pluralize(
                                                                deletionStats.ignored_contacts,
                                                                'contact',
                                                            )}{' '}
                                                            cannot be deleted
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="w-4.5">&nbsp;</span>
                                                        <div className="text-gray-600">
                                                            Contacts with transactions processed
                                                            through Givebutter cannot be deleted.
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </AlertDialog.Body>
                            <AlertDialog.Footer>
                                <AlertDialog.Cancel>Close</AlertDialog.Cancel>
                                {hasAny && (
                                    <Form.SubmitButton
                                        color="error"
                                        className="w-1/2"
                                        disabled={isDeleting}
                                    >
                                        Delete Data
                                    </Form.SubmitButton>
                                )}
                            </AlertDialog.Footer>
                        </AlertDialog.Content>
                    </form>
                </Form.GivebutterFormProvider>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

const DeleteImportAlertDialogTrigger = AlertDialog.Trigger;

const Root = DeleteImportAlertDialog;
const Trigger = DeleteImportAlertDialogTrigger;

export {
    Root,
    Trigger,
    //
    DeleteImportAlertDialog,
    DeleteImportAlertDialogTrigger,
};
export type { DeleteImportAlertDialogProps };
